import { useState, useEffect } from 'react'
import { DateTime } from 'luxon'
import AuthService from 'services/auth'
import CashFlow from 'services/CashFlow'
import { isCanceled, getApiErrorMessage, logApiError } from 'services/apiError'
import useAbortController from 'hooks/useAbortController'
import ItemTabs from 'components/ItemTabs'
import { formatPercentage } from 'pages/Financial/Dre/utils'
import { ReactComponent as Loading } from 'icons/loading2.svg'

import {
  ExpensesContainer,
  Section,
  SectionTitle,
  HintText,
  CardsRow,
  Card,
  CardLabel,
  CardMoney,
  CardHint,
  TableWrapper,
  Table,
  HeaderCell,
  LabelCell,
  ValueCell,
  MoneyText,
  PercentageText,
  LoadingContainer,
  InfoText,
  WarningText,
  VALUE_COLUMN_WIDTH,
  LABEL_COLUMN_WIDTH
} from 'pages/Financial/Dre/styles'

const NEXT_MONTH = 'next'

// Sem teto as tabelas esticam com a resolução e as colunas de dia ficam largas demais em telas grandes.
const MAX_CONTENT_WIDTH = 1440

// Folga de cada lado em telas menores, para o conteúdo não encostar na borda da janela.
const SIDE_MARGIN = 16
const RATE_COLUMN_WIDTH = 80
const TOTAL_COLUMN_WIDTH = 124
const SHARE_COLUMN_WIDTH = 80

const formatDay = isoDate => DateTime.fromISO(isoDate).toFormat('dd/MM')

// A taxa efetiva tem duas casas (2,89%): uma só esconderia a diferença entre 2,89% e 2,94%.
const formatRate = rate => (rate === null ? '–' : `${(rate * 100).toFixed(2).replace('.', ',')}%`)

// Zero é o comum (a maioria das formas não vende nem recebe todo dia) e 31 colunas de "R$ 0,00" escondem o resto.
const renderMoney = (value, kind) => (value === 0 ? '–' : <MoneyText kind={kind} value={value}/>)

// A semana de hoje quando o mês é o atual; senão, a primeira.
const defaultWeek = weeks => {
  const today = DateTime.now().toISODate()

  return (weeks.find(week => today >= week.start && today <= week.end) ?? weeks[0]).key
}

function MethodTable({ title, hint, emptyText, days, table, closing, showShare }) {
  return (
    <Section>
      <SectionTitle>{title}</SectionTitle>
      <HintText>{hint}</HintText>
      {table.rows.length === 0 ? (
        <HintText>{emptyText}</HintText>
      ) : (
        <TableWrapper framed>
          <Table>
            <colgroup>
              <col style={{ width: LABEL_COLUMN_WIDTH }}/>
              <col style={{ width: RATE_COLUMN_WIDTH }}/>
              {days.map(day => <col key={day.date} style={{ width: VALUE_COLUMN_WIDTH }}/>)}
              <col style={{ width: TOTAL_COLUMN_WIDTH }}/>
              {showShare && <col style={{ width: SHARE_COLUMN_WIDTH }}/>}
            </colgroup>
            <thead>
              <tr>
                <HeaderCell sticky align='left'>Forma de pagamento</HeaderCell>
                <HeaderCell>Taxa</HeaderCell>
                {days.map(day => <HeaderCell key={day.date}>{day.label}</HeaderCell>)}
                <HeaderCell>Total</HeaderCell>
                {showShare && <HeaderCell>%</HeaderCell>}
              </tr>
            </thead>
            <tbody>
              {table.rows.map(row => (
                <tr key={row.key}>
                  <LabelCell>{row.label}</LabelCell>
                  <ValueCell><PercentageText>{formatRate(row.rate)}</PercentageText></ValueCell>
                  {row.values.map((value, index) => (
                    <ValueCell key={days[index].date}>{renderMoney(value, 'total')}</ValueCell>
                  ))}
                  <ValueCell kind='total'>{renderMoney(row.total, 'total')}</ValueCell>
                  {showShare && (
                    <ValueCell>
                      <PercentageText>{row.share === null ? '–' : formatPercentage(row.share)}</PercentageText>
                    </ValueCell>
                  )}
                </tr>
              ))}
              {closing.map(line => (
                <tr key={line.label}>
                  <LabelCell kind='total'>{line.label}</LabelCell>
                  <ValueCell kind='total'/>
                  {line.values.map((value, index) => (
                    <ValueCell key={days[index].date} kind='total'>
                      {renderMoney(value, line.signed ? undefined : 'total')}
                    </ValueCell>
                  ))}
                  <ValueCell kind='total'>
                    {line.total !== null && renderMoney(line.total, line.signed ? undefined : 'total')}
                  </ValueCell>
                  {showShare && (
                    <ValueCell kind='total'>
                      {line.share && <PercentageText>{formatPercentage(line.share)}</PercentageText>}
                    </ValueCell>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      )}
    </Section>
  )
}

const sum = values => Math.round(values.reduce((total, value) => total + value, 0) * 100) / 100

/*
  O fluxo de caixa dos recebíveis, numa tela só e em duas tabelas com as mesmas colunas de dia:

  - "Vendas por forma de pagamento": o que foi vendido em cada dia, pelo dia da VENDA.
  - "Entra na conta": o mesmo dinheiro, mas no dia em que ele CAI, pelo prazo de recebimento de cada
    maquininha. Um Pix de dia 27 com prazo de 2 dias fica na coluna do dia 29, e a parcela de uma
    venda de meses atrás aparece no dia em que cai. Dinheiro e Pix direto, sem prazo, entram no dia.

  Cada tabela tem a sua requisição (o dia da venda e o dia da queda não são a mesma janela), feitas
  juntas, para o mês inteiro; trocar de semana só muda o recorte, sem pedir de novo.

  A aba do mês seguinte mostra as mesmas duas tabelas para o mês depois do escolhido, para achar o
  recebível que só cai lá. Ela é buscada na primeira vez que se abre, e os cartões de cima seguem
  sendo do mês escolhido.
*/
export default function Receivables({ year, month }) {
  const businessId = AuthService.getBusinessId()
  const requestAuth = AuthService.getRequestAuth()
  const { abortarPendentes } = useAbortController()

  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState(null)
  const [sales, setSales] = useState(null)
  const [landing, setLanding] = useState(null)
  const [selected, setSelected] = useState(null)
  const [next, setNext] = useState(null)
  const [nextLoading, setNextLoading] = useState(false)
  const [nextError, setNextError] = useState(null)

  useEffect(() => {
    getData()
  }, [year, month])

  const fetchMonth = async (targetYear, targetMonth, signal) => {
    const { startDate, endDate } = CashFlow.monthRange(targetYear, targetMonth)
    const [salesData, landingData] = await Promise.all([
      CashFlow.getReceivables({ businessId, billStart: startDate, billEnd: endDate, requestAuth, signal }),
      CashFlow.getReceivables({ businessId, startDate, endDate, byMethod: true, requestAuth, signal })
    ])

    return { sales: salesData, landing: landingData }
  }

  const nextMonth = DateTime.local(Number(year), Number(month), 1).plus({ months: 1 })

  const loadNext = async () => {
    const signal = abortarPendentes()

    try {
      setNextLoading(true)
      setNextError(null)
      setNext(await fetchMonth(nextMonth.year, nextMonth.month, signal))
    } catch (error) {
      if (isCanceled(error)) return
      logApiError(error)
      setNextError(getApiErrorMessage(error, 'Não foi possível carregar os recebimentos.'))
    } finally {
      if (!signal.aborted) setNextLoading(false)
    }
  }

  const handleTabChange = value => {
    setSelected(value)
    if (value === NEXT_MONTH && !next) loadNext()
  }

  const getData = async () => {
    const signal = abortarPendentes()

    try {
      setLoading(true)
      setErrorMessage(null)
      setSelected(null)
      setNext(null)
      setNextError(null)

      const data = await fetchMonth(year, month, signal)

      setSales(data.sales)
      setLanding(data.landing)
    } catch (error) {
      if (isCanceled(error)) return
      logApiError(error)
      setErrorMessage(getApiErrorMessage(error, 'Não foi possível carregar os recebimentos.'))
    } finally {
      if (!signal.aborted) setLoading(false)
    }
  }

  if (loading) {
    return (
      <LoadingContainer>
        <Loading/>
        <InfoText>Calculando recebimentos...</InfoText>
      </LoadingContainer>
    )
  }

  if (errorMessage) {
    return (
      <LoadingContainer>
        <WarningText>{errorMessage}</WarningText>
      </LoadingContainer>
    )
  }

  const weeks = CashFlow.groupByWeek([], year, month)
  const current = selected ?? defaultWeek(weeks)
  const onNext = current === NEXT_MONTH
  const nextRange = CashFlow.monthRange(nextMonth.year, nextMonth.month)
  const range = onNext ? { start: nextRange.startDate, end: nextRange.endDate } : weeks.find(week => week.key === current)
  const days = CashFlow.daysOf(range.start, range.end)

  // Os dados do período aberto: os do mês escolhido, ou os do seguinte quando a aba dele está aberta.
  const view = onNext ? next : { sales, landing }

  const nextName = nextMonth.setLocale('pt-BR').toFormat('LLLL')
  const nextYearSuffix = nextMonth.year === Number(year) ? '' : `/${nextMonth.year}`
  const weekTabs = [
    ...weeks.map(week => ({ label: `${formatDay(week.start)} a ${formatDay(week.end)}`, value: week.key })),
    { label: `${nextName.charAt(0).toUpperCase()}${nextName.slice(1)}${nextYearSuffix}`, value: NEXT_MONTH }
  ]

  const soldTable = view && CashFlow.buildMethodTable({ byDate: view.sales.byDate, days, field: 'gross' })
  const landingTable = view && CashFlow.buildMethodTable({ byDate: view.landing.byDate, days, field: 'net' })
  const settlement = view && CashFlow.settlementRows({ byDate: view.landing.byDate, days })
  const difference = Math.round((landing.total.received - landing.total.net) * 100) / 100

  const soldClosing = view && [
    { label: 'Total vendido', values: soldTable.totals, total: soldTable.total, share: soldTable.total > 0 ? 1 : null }
  ]
  const landingClosing = view && [
    { label: 'A receber', values: settlement.receivable, total: sum(settlement.receivable) },
    { label: 'Recebido', values: settlement.received, total: sum(settlement.received) },
    { label: 'Diferença', values: settlement.difference, total: sum(settlement.difference), signed: true },
    { label: 'Acumulado no mês', values: settlement.accumulated, total: null }
  ]

  return (
    <ExpensesContainer style={{ maxWidth: MAX_CONTENT_WIDTH, width: `calc(100% - ${SIDE_MARGIN * 2}px)` }}>
      <Section>
        <SectionTitle>O que foi vendido e quando cai na conta</SectionTitle>
        <HintText>
          Em cima, o que foi vendido em cada dia. Embaixo, em que dia esse dinheiro entra na conta, líquido da taxa e
          pelo prazo de recebimento de cada maquininha. Dinheiro e Pix direto entram no dia da venda.
        </HintText>
        {landing.items.some(item => item.estimated) && (
          <HintText>
            Alguns recebimentos usam o prazo padrão (Pix no mesmo dia, débito em 1 dia, crédito em 30 dias por parcela)
            por falta de prazo cadastrado. Informe o prazo de cada maquininha em Configurações &gt; Maquininhas.
          </HintText>
        )}
        <CardsRow>
          <Card>
            <CardLabel>Vendido</CardLabel>
            <CardMoney value={landing.total.net}/>
            <CardHint>Cai na conta neste mês</CardHint>
          </Card>
          <Card>
            <CardLabel>Recebido</CardLabel>
            <CardMoney value={landing.total.received}/>
            <CardHint>Já caiu na conta</CardHint>
          </Card>
          <Card>
            <CardLabel>A receber</CardLabel>
            <CardMoney value={difference} tone={difference < 0 ? '#FF3939' : undefined}/>
            <CardHint>{difference < 0 ? 'Ainda vai cair' : 'Tudo recebido'}</CardHint>
          </Card>
        </CardsRow>
      </Section>
      {/* As abas ficam mesmo num mês vazio: o recebível pode cair só no seguinte. */}
      <ItemTabs
        tabs={weekTabs}
        selectedTab={current}
        tabWidth={130}
        onTabChange={handleTabChange}
        containerStyle={{ padding: 0 }}
      />
      {sales.byDate.length === 0 && landing.byDate.length === 0 && !onNext ? (
        <HintText>Nenhuma venda nem recebimento neste mês.</HintText>
      ) : (
        <>
          {onNext && nextLoading && (
            <LoadingContainer>
              <Loading/>
              <InfoText>Calculando recebimentos...</InfoText>
            </LoadingContainer>
          )}
          {onNext && nextError && <WarningText>{nextError}</WarningText>}
          {view && (
            <>
              <MethodTable
                title='Vendas por forma de pagamento'
                hint='Bruto, no dia em que a comanda foi fechada. A taxa é a efetiva do período.'
                emptyText='Nenhuma venda neste período.'
                days={days}
                table={soldTable}
                closing={soldClosing}
                showShare
              />
              <MethodTable
                title='Entra na conta'
                hint='Líquido, no dia em que o dinheiro cai. Inclui parcelas de vendas de dias ou meses anteriores.'
                emptyText='Nada cai na conta neste período.'
                days={days}
                table={landingTable}
                closing={landingClosing}
              />
            </>
          )}
        </>
      )}
    </ExpensesContainer>
  )
}
